import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import {
  Users,
  Building,
  Mail,
  Phone,
  DollarSign,
  MessageSquare,
  Calendar,
  FileText,
  TrendingUp
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const CRMPage = () => {
  const [funders, setFunders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed-projects`, {}, { headers: { Authorization: `Bearer ${token}` } });
        const projectsRes = await axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } });
        const projectsList = projectsRes.data.projects || [];
        setProjects(projectsList);
        
        // Generate funders from project data
        const funderData = projectsList.slice(0, 6).map((p, idx) => ({
          id: `funder-${idx}`,
          name: `${p.region} Investment Fund`,
          type: ['DFI', 'Private Equity', 'Sovereign Wealth', 'Impact Fund'][idx % 4],
          total_commitment: p.budget_total * 2,
          deployed: p.budget_spent,
          country: p.country,
          sectors: [p.sector],
          status: 'active'
        }));
        setFunders(funderData);
        
        // Generate vendors from project data
        const vendorData = projectsList.slice(0, 8).map((p, idx) => ({
          id: `vendor-${idx}`,
          name: `${p.industry || p.sector} Solutions`,
          country: p.country,
          services: [p.industry || p.sector],
          performance_score: 75 + Math.floor(Math.random() * 20),
          risk_rating: 'low'
        }));
        setVendors(vendorData);
      } catch (error) {
        console.error("Error fetching CRM data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const formatCurrency = (val) => {
    if (val >= 1000000000) return `$${(val / 1000000000).toFixed(2)}B`;
    if (val >= 1000000) return `$${(val / 1000000).toFixed(0)}M`;
    return `$${val?.toLocaleString() || 0}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-[#c9a227] text-xl">Loading CRM data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="crm-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
          CRM Module
        </h1>
        <p className="text-slate-400 mt-1">Manage funder and vendor relationships</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-[#c9a227]" />
              <div>
                <p className="text-2xl font-bold text-[#c9a227]">{funders.length}</p>
                <p className="text-sm text-slate-400">Funders</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Building className="w-8 h-8 text-blue-400" />
              <div>
                <p className="text-2xl font-bold text-blue-400">{vendors.length}</p>
                <p className="text-sm text-slate-400">Vendors</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-2xl font-bold text-green-400">
                  {formatCurrency(funders.reduce((sum, f) => sum + (f.total_committed || 0), 0))}
                </p>
                <p className="text-sm text-slate-400">Total Committed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-purple-400" />
              <div>
                <p className="text-2xl font-bold text-purple-400">
                  {formatCurrency(funders.reduce((sum, f) => sum + (f.disbursed || 0), 0))}
                </p>
                <p className="text-sm text-slate-400">Disbursed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="funders" className="w-full">
        <TabsList className="bg-slate-900/50 border border-slate-700 p-1">
          <TabsTrigger value="funders" className="data-[state=active]:bg-[#c9a227]/20 data-[state=active]:text-[#c9a227]">
            Funders ({funders.length})
          </TabsTrigger>
          <TabsTrigger value="vendors" className="data-[state=active]:bg-[#c9a227]/20 data-[state=active]:text-[#c9a227]">
            Vendors ({vendors.length})
          </TabsTrigger>
          <TabsTrigger value="communications" className="data-[state=active]:bg-[#c9a227]/20 data-[state=active]:text-[#c9a227]">
            Communications
          </TabsTrigger>
        </TabsList>

        {/* Funders Tab */}
        <TabsContent value="funders" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {funders.map((funder) => (
              <Card key={funder.id} className="glass-card">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-sm bg-[#c9a227]/20 flex items-center justify-center">
                        <Users className="w-6 h-6 text-[#c9a227]" />
                      </div>
                      <div>
                        <CardTitle className="text-lg text-white">{funder.name}</CardTitle>
                        <p className="text-sm text-slate-400">{funder.type}</p>
                      </div>
                    </div>
                    <Badge className="status-green">{funder.status}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-500">Total Committed</p>
                      <p className="text-xl font-bold text-[#c9a227]">{formatCurrency(funder.total_committed)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Disbursed</p>
                      <p className="text-xl font-bold text-green-400">{formatCurrency(funder.disbursed)}</p>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs text-slate-500">Disbursement Progress</p>
                      <span className="text-xs text-slate-400">
                        {Math.round((funder.disbursed / funder.total_committed) * 100)}%
                      </span>
                    </div>
                    <Progress 
                      value={(funder.disbursed / funder.total_committed) * 100} 
                      className="h-2 bg-slate-700" 
                    />
                  </div>
                  <div className="pt-3 border-t border-slate-700 space-y-2 text-sm">
                    <p className="text-slate-400 flex items-center gap-2">
                      <Mail className="w-4 h-4" /> {funder.email}
                    </p>
                    <p className="text-slate-400 flex items-center gap-2">
                      <Users className="w-4 h-4" /> {funder.contact_person}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Vendors Tab */}
        <TabsContent value="vendors" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vendors.map((vendor) => (
              <Card key={vendor.id} className="glass-card">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-sm bg-blue-500/20 flex items-center justify-center">
                        <Building className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <CardTitle className="text-base text-white">{vendor.name}</CardTitle>
                        <p className="text-xs text-slate-400">{vendor.country}</p>
                      </div>
                    </div>
                    <Badge className={vendor.risk_rating === 'low' ? 'status-green' : 'status-yellow'}>
                      {vendor.risk_rating}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-wrap gap-1">
                    {vendor.services?.map((s, idx) => (
                      <Badge key={idx} className="bg-slate-700 text-slate-300 text-xs">{s}</Badge>
                    ))}
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs text-slate-500">Performance</p>
                      <span className="text-[#c9a227] font-semibold">{vendor.performance_score}</span>
                    </div>
                    <Progress value={vendor.performance_score} className="h-2 bg-slate-700" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Communications Tab */}
        <TabsContent value="communications" className="mt-6">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-slate-200">Communication Log</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { type: 'Meeting', entity: 'Africa Development Bank', date: '2026-01-15', summary: 'Q1 portfolio review and disbursement planning' },
                  { type: 'Email', entity: 'World Bank IFC', date: '2026-01-12', summary: 'ESG reporting requirements discussion' },
                  { type: 'Call', entity: 'Sovereign Wealth Partners', date: '2026-01-10', summary: 'New investment opportunities presentation' },
                  { type: 'Report', entity: 'Green Climate Fund', date: '2026-01-08', summary: 'Climate impact assessment submission' },
                  { type: 'Meeting', entity: 'AfriConstruct Holdings', date: '2026-01-05', summary: 'Contract renewal negotiations' }
                ].map((comm, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 bg-slate-800/30 rounded-sm">
                    <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${
                      comm.type === 'Meeting' ? 'bg-blue-500/20' :
                      comm.type === 'Email' ? 'bg-purple-500/20' :
                      comm.type === 'Call' ? 'bg-green-500/20' : 'bg-[#c9a227]/20'
                    }`}>
                      {comm.type === 'Meeting' && <Calendar className="w-5 h-5 text-blue-400" />}
                      {comm.type === 'Email' && <Mail className="w-5 h-5 text-purple-400" />}
                      {comm.type === 'Call' && <Phone className="w-5 h-5 text-green-400" />}
                      {comm.type === 'Report' && <FileText className="w-5 h-5 text-[#c9a227]" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-white font-medium">{comm.entity}</p>
                        <span className="text-xs text-slate-500">{comm.date}</span>
                      </div>
                      <Badge className="mt-1 bg-slate-700 text-slate-300 text-xs">{comm.type}</Badge>
                      <p className="text-sm text-slate-400 mt-2">{comm.summary}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CRMPage;
